import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { markNis2, ticketNis2Query } from '../api/nis2'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'

/**
 * NIS2 Art. 23 din panoul incidentului: marcheaza tichetul incidentului ca
 * incident semnificativ (registrul de pe fisa tichetului, `TicketNis2Art23`),
 * cu "a aflat" implicit la prima detectie; marcat, arata termenul urmator.
 */
export function IncidentNis2({ ticket, firstDetectedUtc }: { ticket: number; firstDetectedUtc: string }) {
  const query = ticketNis2Query(ticket)
  const { data } = useQuery({ ...query, retry: false })
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [awareAt, setAwareAt] = useState('')
  const mark = useMutation({
    mutationFn: () => markNis2(ticket, awareAt),
    onSuccess: (page) => {
      client.setQueryData(query.queryKey, page)
      setOpen(false)
      const early = page.report?.stages[0]
      toast(`Ticket #${ticket} is a significant incident${early ? `: early warning due ${early.due}` : ''}.`, 'NIS2 Art. 23', 'success')
    },
    onError: (e: Error) => toast(e.message, 'NIS2 Art. 23', 'error'),
  })

  if (!data?.ready) return null

  if (data.report !== null) {
    const next = data.report.stages.find((s) => s.sent_at === null)
    return (
      <span className="incident-nis2">
        <a className="label label-danger" href={`/app/easeedesk/ticket/${ticket}`}>
          NIS2 Art. 23
        </a>{' '}
        {next ? (
          <span className="text-muted">
            {next.label} due {next.due} {next.state === 'overdue' && <span className="label label-danger">overdue</span>}
          </span>
        ) : (
          <span className="text-muted">all reports sent</span>
        )}
      </span>
    )
  }

  if (!data.canEdit) return null

  return (
    <span className="incident-nis2">
      <button
        type="button"
        className="btn btn-xs btn-default"
        onClick={() => {
          setAwareAt(localOf(firstDetectedUtc))
          setOpen(true)
        }}
      >
        NIS2 Art. 23
      </button>
      <Modal
        id={`incident-nis2-${ticket}`}
        open={open}
        onClose={() => setOpen(false)}
        title="Significant incident (NIS2 Art. 23)?"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger" disabled={mark.isPending} onClick={() => mark.mutate()}>
              Mark significant
            </button>
          </>
        }
      >
        <p>
          Ticket #{ticket} gets the three DNSC deadlines: early warning in 24 h, notification in 72 h, final report a month after the
          notification. The texts are prefilled from this incident's detections; the company reports them in PNRISC.
        </p>
        <label htmlFor={`incident-nis2-aware-${ticket}`}>When did you learn of it?</label>
        <input
          id={`incident-nis2-aware-${ticket}`}
          type="datetime-local"
          className="form-control"
          value={awareAt}
          onChange={(e) => setAwareAt(e.target.value)}
        />
      </Modal>
    </span>
  )
}

/** `2026-10-30 08:02:00` (UTC) in forma lui `datetime-local`, ora browserului. */
function localOf(utc: string): string {
  const d = new Date(`${utc.replace(' ', 'T')}Z`)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
